import React, { useState, useRef, useEffect } from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';
import './InlineSpeechRecognition.css'; // Reuse the same CSS

/**
 * Whisper Recording Component - Simplified to match InlineSpeechRecognition
 *
 * Records audio and transcribes it using OpenAI's Whisper API
 */
const WhisperRecording = ({
  sessionId,
  targetLanguage,
  onTranscriptionComplete,
  autoSend = false,
  onSend,
  onRecordingStateChange
}) => {
  // State
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // References
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Notify parent component when recording state changes
  useEffect(() => {
    if (onRecordingStateChange) {
      onRecordingStateChange(isRecording);
    }
  }, [isRecording, onRecordingStateChange]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, []);

  // This function handles the complete audio recording and resampling process
const recordAndResampleAudio = async () => {
  try {
    // Step 1: Set up high-quality audio constraints
    const constraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1
      }
    };

    // Step 2: Get audio stream
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    streamRef.current = stream;

    // Step 3: Create an AudioContext (browser will determine the native sample rate)
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    console.log(`Original sample rate: ${audioContext.sampleRate}Hz`);

    // Step 4: Create a MediaRecorder to capture the audio
    const mediaRecorder = new MediaRecorder(stream);
    // Store the media recorder in the ref
    mediaRecorderRef.current = mediaRecorder;
    // Reset the audio chunks
    audioChunksRef.current = [];

    // Step 5: Set up the recording process
    mediaRecorder.ondataavailable = (event) => {
      console.log(`Audio data received: ${event.data.size} bytes`);
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    // Step 6: Set up the completion handler with resampling
    mediaRecorder.onstop = async () => {
      // Create blob from the recorded chunks
      const originalBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

      try {
        // Convert the blob to an ArrayBuffer
        const arrayBuffer = await originalBlob.arrayBuffer();

        // Decode the audio data
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // Create an offline context for resampling to 16kHz
        const offlineContext = new OfflineAudioContext(
          1, // mono channel
          audioBuffer.duration * 16000, // new length based on 16kHz
          16000 // target sample rate for Whisper
        );

        // Create a buffer source
        const source = offlineContext.createBufferSource();
        source.buffer = audioBuffer;

        // Add a gain node to increase volume
        const gainNode = offlineContext.createGain();
        gainNode.gain.value = 2.5; // Increase volume significantly

        // Add a high-pass filter to reduce low frequency noise
        const highPassFilter = offlineContext.createBiquadFilter();
        highPassFilter.type = "highpass";
        highPassFilter.frequency.value = 80; // Filter out low rumble

        // Add a compressor to even out audio levels
        const compressor = offlineContext.createDynamicsCompressor();
        compressor.threshold.value = -50;
        compressor.knee.value = 40;
        compressor.ratio.value = 12;
        compressor.attack.value = 0;
        compressor.release.value = 0.25;

        // Connect the audio processing chain
        source.connect(highPassFilter);
        highPassFilter.connect(gainNode);
        gainNode.connect(compressor);
        compressor.connect(offlineContext.destination);

        // Start the source
        source.start(0);

        // Render the audio
        const resampledBuffer = await offlineContext.startRendering();

        // Convert the resampled buffer to a WAV file
        const resampledWav = bufferToWav(resampledBuffer);

        // Create a Blob with the WAV data
        const resampledBlob = new Blob([resampledWav], { type: 'audio/wav' });

        console.log(`Resampled audio size: ${resampledBlob.size} bytes`);
        console.log(`Resampled to 16kHz`);

        // Send this optimized blob to Whisper API
        await transcribeAudio(resampledBlob);
      } catch (error) {
        console.error('Error during audio resampling:', error);
        // Fallback to original audio if resampling fails
        await transcribeAudio(originalBlob);
      }
    };

    // Start recording
    mediaRecorder.start();

    // Return the mediaRecorder so it can be stopped later
    return mediaRecorder;
  } catch (error) {
    console.error('Error setting up audio recording:', error);
    throw error;
  }
};

// Function to convert AudioBuffer to WAV format
function bufferToWav(buffer) {
  const numOfChannels = buffer.numberOfChannels;
  const length = buffer.length * numOfChannels * 2;
  const sampleRate = buffer.sampleRate;

  // Create the WAV file
  const wav = new DataView(new ArrayBuffer(44 + length));

  // Write WAV header
  // "RIFF" chunk descriptor
  writeString(wav, 0, 'RIFF');
  wav.setUint32(4, 36 + length, true);
  writeString(wav, 8, 'WAVE');

  // "fmt " sub-chunk
  writeString(wav, 12, 'fmt ');
  wav.setUint32(16, 16, true); // subchunk size
  wav.setUint16(20, 1, true); // PCM format
  wav.setUint16(22, numOfChannels, true); // num channels
  wav.setUint32(24, sampleRate, true); // sample rate
  wav.setUint32(28, sampleRate * numOfChannels * 2, true); // byte rate
  wav.setUint16(32, numOfChannels * 2, true); // block align
  wav.setUint16(34, 16, true); // bits per sample

  // "data" sub-chunk
  writeString(wav, 36, 'data');
  wav.setUint32(40, length, true); // subchunk size

  // Write audio data
  const channels = [];
  for (let i = 0; i < numOfChannels; i++) {
    channels.push(buffer.getChannelData(i));
  }

  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let j = 0; j < numOfChannels; j++) {
      const sample = Math.max(-1, Math.min(1, channels[j][i]));
      // Convert float to int16
      const value = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      wav.setInt16(offset, value, true);
      offset += 2;
    }
  }

  return wav;
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

const diagnoseAudio = async (audioBlob) => {
  // Log audio properties
  console.log(`Audio size: ${audioBlob.size} bytes`);
  console.log(`Audio type: ${audioBlob.type}`);

  // Create an audio element to test playback
  const audioURL = URL.createObjectURL(audioBlob);
  const audio = new Audio(audioURL);

  // Optional: Save the audio for manual comparison
  const a = document.createElement('a');
  a.href = audioURL;
  a.download = 'test-recording.webm';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  return audioBlob;
};

  // Start recording
  const startRecording = async () => {
  try {
    setIsProcessing(true);

    // Use the new resampling recorder function
    await recordAndResampleAudio();

    setIsRecording(true);
    setIsProcessing(false);
  } catch (error) {
    console.error('Error starting recording:', error);
    setError('Failed to access microphone');
    setIsProcessing(false);
  }
};

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      setIsProcessing(true);
      mediaRecorderRef.current.stop();
    }

    // Stop the stream tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Reset media recorder
    mediaRecorderRef.current = null;
    setIsRecording(false);
  };

  // Transcribe audio using Whisper API
  const transcribeAudio = async (audioBlob) => {
    if (!audioBlob || audioBlob.size === 0) {
      setError('No audio recorded');
      //window.alert('no audio recorded');
      setIsProcessing(false);
      return;
    }

    try {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      console.log(`Device: ${isIOS ? 'iOS' : 'non-iOS'}, Blob size: ${audioBlob.size}, type: ${audioBlob.type}`);

      // Create form data
      const formData = new FormData();

      // For iOS, we need to be more careful with the audio format and naming
      if (isIOS) {
        // Create a new blob with an explicit type
        let iosBlob;

        // Try to convert the type if needed
        if (audioBlob.type !== 'audio/mp3' && audioBlob.type !== 'audio/mpeg') {
          try {
            // Create a new blob with an explicit MIME type that's better supported on iOS
            iosBlob = new Blob([await audioBlob.arrayBuffer()], { type: 'audio/mpeg' });
            console.log('Converted blob to audio/mpeg for iOS compatibility');
          } catch (conversionError) {
            console.error('Error converting blob type:', conversionError);
            iosBlob = audioBlob;
          }
        } else {
          iosBlob = audioBlob;
        }

        // Use File constructor with explicit filename instead of FormData.append
        const file = new File([iosBlob], 'recording.mp3', { type: 'audio/mpeg' });
        formData.append('audio', file);

        // Log details of the created file
        console.log(`iOS File created: ${file.name}, size: ${file.size}, type: ${file.type}`);
      } else {
        // Non-iOS: use regular approach
        formData.append('audio', audioBlob, 'recording.mp3');
      }

      formData.append('sessionId', sessionId);
      formData.append('language', targetLanguage);

      // Log all form data entries to verify they're properly added
      console.log('FormData entries:');
      try {
        for (let pair of formData.entries()) {
          console.log(`- ${pair[0]}: ${pair[0] === 'audio' ? 'File/Blob' : pair[1]}`);
        }
      } catch (e) {
        console.log('Could not enumerate FormData entries:', e);
      }

      console.log("Attempting to send to Whisper API...");

      // Fallback to simpler approach for iOS
      if (isIOS) {
        try {
          console.log('iOS: Using axios normally but with smaller audio file');

          // For severely problematic iOS versions, reduce audio quality further if needed
          if (audioBlob.size > 500000) { // If more than 500KB
            console.log('Large audio file detected on iOS, compressing further');
            //window.alert('Audio file is large, compressing for iOS compatibility');

            // Create an audio element to play and re-record at lower quality
            const audioURL = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioURL);

            // Just proceed with the existing audio but using axios directly
            // with specific options that work better on iOS
          }

          // Use the normal apiClient but with adjusted options
          const response = await apiClient.audio.transcribeWhisper(formData);

          const transcription = response.data.transcription;
          const audioId = response.data.audio_id;

          if (onTranscriptionComplete) {
            onTranscriptionComplete(transcription, audioId);
          }

          if (autoSend && transcription && transcription.trim() !== '') {
            if (onSend) {
              onSend(transcription, audioId);
            }
          }
        } catch (iosError) {
          console.error('iOS axios approach failed:', iosError);
          throw iosError; // Rethrow to be caught by outer catch block
        }
      } else {
        // Use normal apiClient for non-iOS
        const response = await apiClient.audio.transcribeWhisper(formData);

        const transcription = response.data.transcription;
        const audioId = response.data.audio_id;

        if (onTranscriptionComplete) {
          onTranscriptionComplete(transcription, audioId);
        }

        if (autoSend && transcription && transcription.trim() !== '') {
          if (onSend) {
            onSend(transcription, audioId);
          }
        }
      }

    } catch (error) {
      console.error('Error transcribing audio:', error);

      // Detailed error info
      let errorMsg = 'Error transcribing audio: ';
      if (error.isAxiosError) {
        errorMsg += `Axios: ${error.message}`;

        if (error.request && !error.response) {
          errorMsg += ' (Request made but no response received)';
        }

        if (error.response) {
          errorMsg += ` Status: ${error.response.status}`;
        }
      } else {
        errorMsg += error.message || error;
      }

      //window.alert(errorMsg);
      setError('Failed to transcribe audio');
    } finally {
      setIsProcessing(false);
    }
  };

  // Toggle recording
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <Tooltip title={isRecording ? "Stop recording" : "Start voice recording"}>
      <IconButton
        color={isRecording ? "error" : "secondary"}
        onClick={toggleRecording}
        disabled={isProcessing}
        aria-label={isRecording ? "stop recording" : "start recording"}
        className={isRecording ? "mic-recording" : ""}
        sx={{
          backgroundColor: isRecording ? 'rgba(244, 67, 54, 0.1)' : 'transparent',
          ...(isRecording && {
            animation: 'pulse 1.5s infinite',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -4,
              left: -4,
              right: -4,
              bottom: -4,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: 'error.main',
              animation: 'pulseBorder 2s infinite'
            }
          }),
          '&:hover': {
            backgroundColor: isRecording ? 'rgba(244, 67, 54, 0.2)' : 'rgba(156, 39, 176, 0.1)',
          }
        }}
      >
        {isProcessing ? (
          <CircularProgress size={24} color="inherit" />
        ) : isRecording ? (
          <MicOffIcon />
        ) : (
          <MicIcon />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default WhisperRecording;